import {
  AxesHelper,
  BufferGeometry,
  Group,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Scene,
  Vector3,
} from 'three';

import { CAMERA_FAR_2D, CAMERA_FAR_3D } from '@/config/render';
import { COMMENT_GREEN, COMMENT_RED, COMMENT_YELLOW } from '@/features/anno/config';
import { VisibleStatus } from '@/features/anno/stores/slices';
import { LineSegmentsMesh, OrbitControls } from '@/lib/three';
import type { Comment, CoordConfig, Label, LoadStatus, Rawdata } from '@/types';

import { AnnoFactory, AnnoInstanceItem, CommentFactory, Cuboid, IAnnoItem } from '../annos';
import { EnhancedObject3D } from '../graphics';
import { Tool, ToolType } from '../tool';
import { AnnoSelects } from './AnnoSelects';
import { IKeyPoint, KeyPoint } from './KeyPoint';
import { Overlays } from './Overlays';
import { RawImage } from './RawImage';
import { RawObject, RawObjectOptions } from './RawObject';
import { RawPoints } from './RawPoints';
import { ThreeViewOptions, ThreeViews } from './ThreeViews';

export type CameraType = 'OrthographicCamera' | 'PerspectiveCamera';

export interface CameraOptions {
  eye: [number, number, number];
  type: CameraType;
  up?: [number, number, number];
  target?: [number, number, number];
  near?: number;
  far?: number;
  fov?: number;
}
export interface ControlsOptions {
  enabled: boolean;
  enableRotate?: boolean;
  panSpeed?: number;
  enableAdaptFar?: boolean;
}

export enum GroupType {
  Container = 'container',
  Addition = 'addition',
  MARKER = 'marker',
}
export interface SightOptions {
  // canvas 初始化数据
  canvas: {
    width: number;
    height: number;
    dom?: HTMLElement;
  };

  // left/bottom/width/height 均为小数表示 canvas 尺寸的相对【占比】，取值范围为[0, 1]
  viewPercent?: [number, number, number, number];

  // 相机与控制器参数
  cameraOptions?: CameraOptions;
  controlsOptions?: ControlsOptions;

  // 子视图配置
  subViewOptions?: ThreeViewOptions[];

  // 渲染方法
  renderMethod: (data: Rawdata) => CoordConfig;
}

const _v = new Vector3();

export class Sight {
  /** 默认图层 / 标注物的图层 */
  static ANNO_LAYER = 0;

  /** 背景文件的图层 */
  static RAW_OBJECT_LAYER = 1;

  /** 辅助类元素 */
  static ADDITION_LAYER = 2;

  /** 标识类元素 */
  static MARKER_LAYER = 3;

  /** 场景唯一ID */
  public id: string;
  /** 当前场景渲染尺寸数据：left/bottom/width/height */
  public viewport: [number, number, number, number];

  public scene: Scene;
  /** 照相机 */
  public camera: OrthographicCamera | PerspectiveCamera;
  /** 控制器 */
  public controls?: OrbitControls;
  /** 选择器 */
  public selects: AnnoSelects;
  /** 坐标系设置 */
  public coord: Required<CoordConfig>;

  /** 场景主要目标物（点云/图片等） */
  public rawObject: RawObject;
  /** 标注物容器 */
  public container: Group;
  /** 辅助交互物体的容器 */
  public addition: Group;
  /** 标识类元素，例如坐标系指示，车姿范围等 */
  public marker: Group;
  /** 子视图 */
  public subViews?: ThreeViews;
  /** 文件源 */
  public dataSource: Rawdata;
  /** 初始化的配置项 */
  public options: Required<SightOptions>;

  public overlays?: Overlays;

  constructor(id: string, data: Rawdata, options: SightOptions) {
    const { type: rawdataType } = data;
    const {
      scene,
      container,
      marker,
      cameraOptions: sceneCamera,
      controlsOptions: sceneControls,
    } = rawdataType === 'image' ? Sight.create2DSence() : Sight.create3DSence();

    const opts: Required<SightOptions> = {
      viewPercent: [0, 0, 1, 1],
      subViewOptions: [],

      ...options,
      controlsOptions: {
        enabled: false,
        ...sceneControls,
        ...options.controlsOptions,
      },
      cameraOptions: {
        ...(sceneCamera as CameraOptions),
        ...options.cameraOptions,
      },
    };

    this.id = id;
    this.scene = scene;
    this.container = container;
    this.container.name = GroupType.Container;
    this.dataSource = data;
    this.options = opts;
    this.addition = new Group();
    this.addition.name = GroupType.Addition;
    this.marker = marker;
    this.marker.name = GroupType.MARKER;

    const api = ((target: Sight) => {
      return (name: string) => target.getObjectByName(name, GroupType.Container);
    })(this);
    this.selects = new AnnoSelects(api);

    this.scene.add(this.addition);

    const { canvas, cameraOptions, controlsOptions, subViewOptions, viewPercent, renderMethod } = opts;

    this.viewport = viewPercent.map((percent, i) =>
      i % 2 === 0 ? Math.floor(canvas.width * percent) : Math.floor(canvas.height * percent)
    ) as [number, number, number, number];

    const { position = [0, 0, 0], scale = [1, 1, 1], matrixs = [new Matrix4(), new Matrix4()] } = renderMethod(data);
    this.coord = { position, scale, matrixs };

    // 初始化文件对象
    this.rawObject = rawdataType === 'image' ? new RawImage(data) : new RawPoints(data);
    this.scene.add(this.rawObject.object);

    this.camera = Sight.createCamera(this.viewport[2], this.viewport[3], cameraOptions);
    // 默认启用所有图层
    this.camera.layers.enableAll();

    // 如场景有控制器，则控制器的默认状态数据(zoom0, position0)为初始化状态，更新时需谨慎
    this.controls = canvas.dom ? Sight.createControls(this.camera, canvas.dom, controlsOptions) : undefined;
    this.subViews =
      subViewOptions.length > 0 ? new ThreeViews(canvas.width, canvas.height, this.scene, subViewOptions) : undefined;

    // 在目标文件加载完成之前隐藏标注框与标识对象
    this.container.visible = false;
    this.marker.visible = false;

    this.updateCameraZoom(this.viewport[2], this.viewport[3]);
  }

  public async loadFile(onLoadProgress?: (data: LoadStatus) => void) {
    try {
      const loadData = await this.rawObject.load(this.dataSource, { coord: this.coord, onProgress: onLoadProgress });
      // 调整相机位置
      loadData?.cameraPosition && this.setCenter(loadData?.cameraPosition);
      // 显示标注框
      this.container.visible = true;
      this.marker.visible = true;

      this.rawObject.object.traverse((obj) => obj.layers.set(Sight.RAW_OBJECT_LAYER));
      this.rawObject.object.visible = true;
    } catch (error: any) {
      onLoadProgress?.({
        info: 'error',
        progress: error.message ?? JSON.stringify(error),
      });
    }
  }

  static create2DSence() {
    const scene = new Scene();
    const container = new Group();
    const marker = new Group();

    scene.add(container, marker);

    return {
      scene,
      container,
      marker,
      cameraOptions: { type: 'OrthographicCamera', eye: [0, 0, CAMERA_FAR_2D + 1], far: CAMERA_FAR_2D + 10 },
      controlsOptions: { enableRotate: false, panSpeed: 0.9 },
    };
  }
  static create3DSence() {
    const scene = new Scene();
    const container = new Group();
    const marker = new Group();

    const axesHelper = new AxesHelper(3);
    // 将坐标系调整至背景图层
    axesHelper.layers.set(Sight.MARKER_LAYER);
    marker.add(axesHelper);

    scene.add(container, marker);

    return {
      scene,
      container,
      marker,
      // cameraOptions: { type: 'OrthographicCamera', eye: [0, 0, CAMERA_FAR_3D / 2], far: CAMERA_FAR_3D },
      cameraOptions: {
        type: 'PerspectiveCamera',
        eye: [0, 0, CAMERA_FAR_3D / 2],
        far: CAMERA_FAR_3D,
      },
      controlsOptions: { panSpeed: 0.4, enableAdaptFar: true },
    };
  }
  static createCamera(width: number, height: number, options: CameraOptions, zoom = 1) {
    const { eye, type, target, up = [0, 0, 1], near = 0.1, far = 400, fov = 75 } = options;
    const aspect = width / height;
    const camera =
      type === 'OrthographicCamera'
        ? new OrthographicCamera(width / -2, width / 2, height / 2, height / -2, near, far)
        : new PerspectiveCamera(fov, aspect, near, far);
    camera.up.set(...up);
    camera.position.set(...eye);
    camera.zoom = zoom;
    if (target) {
      camera.lookAt(...target);
    }
    return camera;
  }
  static createControls(
    camera: OrthographicCamera | PerspectiveCamera,
    domElement: HTMLElement,
    options: ControlsOptions
  ) {
    const controls = new OrbitControls(camera, domElement);
    // 控制器距离最值可能会影响相机的手动定位，尽量不要更改最小值
    controls.minDistance = 1;
    controls.maxDistance = camera.far * 0.875;
    controls.screenSpacePanning = false;
    controls.zoomToCursor = true;

    const { enabled, panSpeed = 1, enableRotate = true, enableAdaptFar = false } = options;
    controls.enabled = enabled;
    controls.enableRotate = enableRotate;
    controls.panSpeed = panSpeed;

    controls.enableAdaptFar = enableAdaptFar;
    controls.minCameraFar = 200;
    controls.minZoom = 1;

    return controls;
  }

  static getLayerFromTool(tool: ToolType) {
    if (Tool.isAnnoTool(tool)) {
      return Sight.ANNO_LAYER;
    }

    if (Tool.isAdditionTool(tool)) {
      return Sight.ADDITION_LAYER;
    }

    return Sight.MARKER_LAYER;
  }

  public updateSceneSize(width: number, height: number) {
    const newViewport = this.options.viewPercent.map((percent, i) =>
      i % 2 === 0 ? Math.floor(width * percent) : Math.floor(height * percent)
    ) as [number, number, number, number];

    // 如果当前视图窗口宽高为0，则当前节点未被真实渲染过，故用新的窗口宽高更新照相机【初始的缩放比例】
    if (this.viewport[2] <= 0 || this.viewport[3] <= 0) {
      this.updateCameraZoom(newViewport[2], newViewport[3]);
    }

    this.updateCameraSize(newViewport[2], newViewport[3]);
    this.subViews?.updateCameraSize(width, height);
    this.viewport = newViewport;

    // 当长宽更改后，需要更新所有 LineSegmentsMesh 相关的内容
    this.container.traverse((object) => {
      if (object instanceof LineSegmentsMesh) {
        object.material.resolution.set(width, height);
      }
    });

    // 如果有 controls 控件，则更新 controls 的视口大小，否则会使 zoomToCursor 失准
    if (this.controls) {
      this.controls.domRect = { width, height };
    }
  }

  private updateCameraSize(width: number, height: number) {
    if ('isOrthographicCamera' in this.camera) {
      this.camera.left = width / -2;
      this.camera.right = width / 2;
      this.camera.top = height / 2;
      this.camera.bottom = height / -2;
    } else {
      this.camera.aspect = width / height;
    }

    this.camera.updateProjectionMatrix();
  }

  /**
   * 根据sight渲染宽高调整zoom，使得文件【初始化】时显示的尺寸合适
   * @param sightWidth 视图宽度
   * @param sightHeight 视图高度
   * @returns
   */
  private updateCameraZoom(sightWidth: number, sightHeight: number) {
    if (sightWidth <= 0 || sightHeight <= 0) return;

    if (this.rawObject.type === 'image') {
      this.camera.zoom = RawImage.getFitZoom(sightWidth, sightHeight, this.dataSource, this.coord.scale);
    } else if (this.rawObject.type === 'pointcloud') {
      this.camera.zoom = 3;
    }

    // 更新相机默认 zoom 值，如果需要页面还原缩放，那么希望缩放到这个值
    if (this.controls) {
      this.controls.zoom0 = this.camera.zoom;
    }
  }

  /**
   * 更新当前场景标注物渲染
   * @param annoList 标注数据列表
   * @param withLabel 是否显示标签
   */
  public updateObjectList(
    annoList: Array<AnnoInstanceItem>,
    labelMap: Map<Label['name'], Label>,
    trackIdsVisibleFlag: Record<IAnnoItem['trackId'], number>,
    withLabel?: boolean
  ) {
    // 从文件到threejs坐标系的转换矩阵
    const transMatrix = this.getMatrixs()[1];

    const annoMap: Map<AnnoInstanceItem['name'], AnnoInstanceItem> = new Map();
    annoList.forEach((item) => annoMap.set(item.name, item));

    const toBeRemoved = this.container.children.filter((child) => {
      const anno = annoMap.get(child.name);
      if (anno) {
        AnnoFactory.updateFromWidget(child, anno.widget, transMatrix);
        const label = labelMap.get(anno.label);
        if (label) {
          AnnoFactory.changeColor(child, label.color);
        }

        const isShow = (trackIdsVisibleFlag[anno.trackId] ?? VisibleStatus.Show) === VisibleStatus.Show;
        if (child.visible !== isShow) {
          child.visible = isShow;
        }
      }
      return !annoMap.delete(child.name);
    });
    if (toBeRemoved.length > 0) {
      this.container.remove(...toBeRemoved);
    }

    const newObjects: Array<Object3D> = [];
    annoMap.forEach((item) => {
      const label = labelMap.get(item.label);
      const isShow = (trackIdsVisibleFlag[item.trackId] ?? VisibleStatus.Show) === VisibleStatus.Show;
      if (label) {
        const obj = AnnoFactory.createFromWidget(item.name, item.widget, transMatrix, {
          color: label.color,
          anchor: withLabel,
          canvasSize: [this.options.canvas.width, this.options.canvas.height],
          ...(item.seriesPoint ? { seriesPoint: item.seriesPoint } : {}),
        });
        if (obj) {
          obj.visible = isShow;
          if (obj.userData.widget.name === 'cuboid' && obj.userData.widget.point_cnt === 0) {
            obj.userData.widget.point_cnt = Cuboid.updatePointCount(
              obj as Mesh<BufferGeometry, MeshBasicMaterial>,
              this.camera,
              this.getCurrentUsingRawObject().object
            );
          }
          if (item.seriesPoint?.isAdsorb) {
            // 渲染时吸附的添加点加入到指示线中
            const indicateLine = newObjects.find((obj) => obj.name === item.seriesPoint?.indicateLine);
            if (indicateLine) {
              indicateLine.add(obj);
              //  parent 标记为引导线
              indicateLine.userData.isIndicateLine = true;
            }
          } else {
            newObjects.push(obj);
          }
        }
      }
    });
    if (newObjects.length > 0) {
      this.container.add(...newObjects);
    }
  }

  public addAddition(target: Object3D) {
    this.addition.add(target);
    target.traverse((obj) => obj.layers.set(Sight.ADDITION_LAYER));
  }

  public addMarker(target: Object3D) {
    this.marker.add(target);
    target.traverse((obj) => obj.layers.set(Sight.MARKER_LAYER));
  }

  /**
   * 更新当前场景漏标的标注物
   * @param annoCommentMap 批注列表
   * @returns
   */
  updateCommentObjects(comments: Comment[] | undefined) {
    // 漏标的批注
    if (!comments || comments.length === 0) return;

    const commentMap = new Map(comments.map((comment) => [comment.uuid, comment]));

    const toBeRemoved = this.container.children.filter((child) => {
      if (child.userData.type !== 'pyramid') return false;
      return !commentMap.delete(child.name);
    });
    if (toBeRemoved.length > 0) {
      this.container.remove(...toBeRemoved);
    }

    const newObjects: Array<Object3D> = [];
    commentMap.forEach((comment) => {
      if (!comment.extra_info?.position) return;
      const obj = CommentFactory.createFromComment(comment, {
        canvasSize: [this.options.canvas.width, this.options.canvas.height],
      });
      if (obj) {
        obj.layers.set(Sight.ANNO_LAYER);
        this.container.add(obj);
      }
    });
    if (newObjects.length > 0) {
      this.addition.add(...newObjects);
    }
  }

  /**
   * 根据批注更新标注物颜色
   * @param annoCommentMap 标注物的批注
   * @param missedComments 当前帧的漏标
   * @param annotations 标注物列表
   * @param labelMap 标签列表
   */
  public updateObjectsByComments(
    annoCommentMap: Record<IAnnoItem['name'], Comment>,
    missedComments: Comment[] | undefined,
    annotations: Record<string, IAnnoItem | null>,
    labelMap: Map<Label['name'], Label>
  ) {
    // 标注物的批注
    this.container.children.forEach((child) => {
      const comment = annoCommentMap[child.name] ?? missedComments?.find((item) => item.uuid === child.name);
      const labelColor = labelMap.get(annotations[child.name]?.label ?? '')?.color;
      // 删除comment
      if (!comment) {
        if (labelColor && child.userData.color !== labelColor) {
          AnnoFactory.changeColor(child, labelColor);
        }
        return;
      }
      if (comment.status === 'unresolved') {
        if (child.userData.color !== COMMENT_RED) {
          AnnoFactory.changeColor(child, COMMENT_RED);
        }
      } else if (comment.status === 'pending') {
        if (child.userData.color !== COMMENT_YELLOW) {
          AnnoFactory.changeColor(child, COMMENT_YELLOW);
        }
      } else {
        // 标注物需要变成标签颜色
        if (labelColor && child.userData.color !== labelColor) {
          AnnoFactory.changeColor(child, labelColor);
          // 漏标需要变成绿色
        } else if (comment.scope === 'unspecified' && child.userData.color !== COMMENT_GREEN) {
          AnnoFactory.changeColor(child, COMMENT_GREEN);
          if (child.userData.status === 'select') {
            AnnoFactory.setStatus(child, child.userData.status);
          }
        }
      }
    });
  }

  /**
   * 更新当前场景观察点列表
   * @param annoList 标注数据列表
   */
  public updateKeypointList(annoList: Array<IKeyPoint>, activeKeyPoint: Pick<IKeyPoint, 'color' | 'name'> | null) {
    // 从文件到threejs坐标系的转换矩阵
    const transMatrix = this.getMatrixs()[1];

    const annoMap: Map<IKeyPoint['name'], IKeyPoint> = new Map();
    annoList.forEach((item) => annoMap.set(item.name, item));

    const toBeRemoved = this.addition.children.filter((child) => {
      if (child.userData.type === 'keypoint') {
        const point = annoMap.get(child.name);
        if (point) {
          KeyPoint.update(child as Mesh<BufferGeometry, MeshBasicMaterial>, point, {
            type: this.rawObject.type,
            transMatrix,
          });
        }
        return !annoMap.delete(child.name);
      } else {
        return false;
      }
    });

    if (toBeRemoved.length > 0) {
      this.addition.remove(...toBeRemoved);
    }

    const newObjects: Array<Object3D> = [];
    annoMap.forEach((point) => {
      const obj = KeyPoint.create(point, { type: this.rawObject.type, transMatrix });
      if (obj) {
        obj.layers.set(Sight.ADDITION_LAYER);
        newObjects.push(obj);
      }
    });

    if (newObjects.length > 0) {
      this.addition.add(...newObjects);
    }

    if (activeKeyPoint) {
      const target = this.addition.getObjectByName(activeKeyPoint.name);
      if (target) {
        EnhancedObject3D.setObjectColor(target, activeKeyPoint.color);
        target.userData.color = activeKeyPoint.color;
      }
    }
  }

  /**
   * 显示/隐藏标注物
   * @param objectsVisible
   */
  public updateObjectsVisible(objectsVisible: Set<IAnnoItem['name']>) {
    this.container.children.forEach((child) => {
      if (objectsVisible.has(child.name)) {
        child.visible = true;
      } else {
        child.visible = false;
      }
    });
  }

  // ----------- 场景标注对象交互 -----------
  public addObjectFromTool(type: ToolType, object: Object3D, containerName?: string) {
    if (containerName) {
      const container = this.getObjectByName(containerName, GroupType.Container);
      container?.add(object);
    } else if (Tool.isAnnoTool(type)) {
      this.container.add(object);
    } else if (Tool.isAdditionTool(type)) {
      this.addAddition(object);
    } else {
      this.addMarker(object);
    }
  }
  public removeObject(object: Object3D) {
    object.parent?.remove(object);
  }

  public removeObjectByName(name: string) {
    const object = this.getObjectByName(name);
    object && this.removeObject(object);
  }

  /**
   * 根据 name 获取标注物的渲染对象
   * @param name Object3D.name
   * @param scope 指定查找范围，无则默认全量（Container + Addition + MARKER）查找
   * @returns
   */
  public getObjectByName(name: string | undefined | null, scope?: GroupType) {
    if (!name) return undefined;

    if (typeof scope === 'string') {
      switch (scope) {
        case GroupType.Container:
          return this.container.getObjectByName(name);
        case GroupType.Addition:
          return this.addition.getObjectByName(name);
        case GroupType.MARKER:
          return this.marker.getObjectByName(name);
        default:
          return undefined;
      }
    }

    return (
      this.container.getObjectByName(name) ?? this.addition.getObjectByName(name) ?? this.marker.getObjectByName(name)
    );
  }

  /**
   * 设置标注物直接子代中的标签渲染顺序
   * @param name
   * @param order
   */
  public setObjectLabelOrder(name: string | undefined | null, order = 0) {
    this.getObjectByName(name)?.children.some((target) => {
      if ('isCSS2DObject' in target && target.isCSS2DObject) {
        target.renderOrder = order;
        return true;
      }
      return false;
    });
  }

  /**
   * 将目标物移动到相机视野中央，保持相机姿态和缩放
   * @param name 目标物 name
   */
  public moveToObject(name: string) {
    const object = this.getObjectByName(name);
    if (object) {
      const target = AnnoFactory.getAnnoCenter(object);
      if (target) {
        this.setCameraToward(...target.toArray());
      }
    }
  }

  // ----------- 场景控制器与相机交互 -----------
  public disableControls() {
    if (this.controls?.enabled) {
      this.controls!.enabled = false;
    }
  }
  public enableControls() {
    if (this.controls) {
      this.controls.enabled = true;
    }
  }
  public disableControlsRotate() {
    if (this.controls) {
      this.controls.enableRotate = false;
    }
  }
  public enableControlsRotate() {
    if (this.controls) {
      this.controls.enableRotate = true;
    }
  }

  public enableControlsVerticalRotate() {
    if (this.controls) {
      this.controls.maxPolarAngle = Math.PI;
    }
  }

  public disableControlsVerticalRotate() {
    if (this.controls) {
      this.controls.maxPolarAngle = 0;
    }
  }
  /**
   * 设置场景中心点坐标
   * @param vec
   */
  private setCenter(center: [number, number, number]) {
    this.camera.position.setX(center[0]);
    this.camera.position.setY(center[1]);
    this.camera.updateProjectionMatrix();

    if (this.controls) {
      this.controls.target.fromArray(center);
      // 在资源加载完成后再次更新初始化状态
      this.controls.saveState();
    }
  }
  /**
   * 更新相机的位置和朝向
   * @param x x 坐标
   * @param y y 坐标
   * @param z z 坐标
   */
  public setCameraToward(x: number, y: number, z: number) {
    const target = this.controls?.target ?? new Vector3(0, 0, 0);
    const delta = _v.subVectors(this.camera.position, target);
    target.set(x, y, z);

    // 设置相对位移的 position ，使 camera 朝向不变
    this.camera.position.copy(_v.addVectors(target, delta));
    this.camera.updateProjectionMatrix();
  }

  /**
   * 得到当前 sight 的相机信息
   */
  public getCameraInfo() {
    const { position, zoom } = this.camera;
    return {
      position: position.toArray(),
      zoom,
      target: this.controls?.target.toArray(),
    };
  }

  /**
   * 设置当前 sight 的相机信息
   */
  public setCameraInfo(info: { position: [number, number, number]; zoom: number; target?: [number, number, number] }) {
    const { position, zoom, target } = info;
    this.camera.position.set(...position);
    this.camera.zoom = zoom;
    target && this.controls?.target.set(...target);
    this.camera.updateProjectionMatrix();
    this.controls?.update();
  }

  /**
   * 更新渲染配置
   * @param options
   */
  public updateRenderOptions(options: RawObjectOptions) {
    this.rawObject.updateRenderOptions(options);
    this.overlays?.updateRenderOptions(options);
  }

  /**
   * 获取当前主要渲染的文件，在叠帧模式下可能与 this.id 不同
   * @returns Rawdata
   */
  public getCurrentFile() {
    return this.overlays ? this.overlays.currentFile : this.dataSource;
  }

  /**
   * 获取当前场景的转换矩阵，在叠帧模式下可能与 this.coord.matrixs 不同
   * @returns [three.js 坐标系 到 文件原始坐标系的矩阵, 文件原始坐标系 到 three.js 坐标系的矩阵]
   */
  public getMatrixs(): Required<CoordConfig>['matrixs'] {
    if (!this.overlays) return this.coord.matrixs;

    return this.overlays.getCurrentMatrixs() ?? this.coord.matrixs;
  }

  /**
   * 初始化叠帧
   * @returns
   */
  public initOverlays() {
    this.overlays = new Overlays(this.dataSource, this.coord, this.rawObject);

    const { background } = this.overlays;
    this.updateRenderOptions(this.rawObject.options);
    this.scene.add(background.object);

    return this.overlays;
  }

  /**
   * 在叠帧模式下更新当前帧
   * @param raw
   * @returns
   */
  public updateCurrentRaw(raw: Rawdata) {
    if (!this.overlays || this.overlays.currentFile === raw) return false;

    const targets = this.overlays.getRenderObjectsByName(raw.name);
    // TO FIX
    if (targets.length === 0) return false;

    const oldChildren = [...this.rawObject.object.children];
    this.rawObject.object.clear();

    targets.forEach((target) => {
      const cloned = target.clone();
      if ('material' in target && target.material instanceof Material) {
        (cloned as any).material = target.material.clone();
      }
      cloned.visible = true;
      this.rawObject.object.add(cloned);
    });

    this.rawObject.updateRenderOptions(this.rawObject.options, true);
    this.overlays.updateCurrent(raw);
    // 释放拷贝出的对象材质内存
    oldChildren.forEach((child: any) => {
      if (child.material) child.material.dispose();
    });

    return true;
  }

  /**
   * 废弃叠帧并释放相关内存
   */
  public destroyOverlays() {
    if (this.overlays) {
      // 恢复原本的文件渲染对象
      this.updateCurrentRaw(this.dataSource);

      const group = this.overlays.background.object;
      this.overlays = undefined;

      this.scene.remove(group);
      AnnoFactory.dispose(group);
    }
  }

  /**
   * 得到当前帧的现有的点云数据, 如果有叠帧则返回叠帧的点云数据
   */
  public getUsingRawObject() {
    return this.overlays?.background ?? this.rawObject;
  }

  /**
   * 得到当前帧的现有的点云数据, 即使有叠帧
   */
  public getCurrentUsingRawObject() {
    return this.rawObject;
  }
}
